import { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import type { Chantier } from '@/types';
import 'leaflet/dist/leaflet.css';

const DEFAULT_CENTER: [number, number] = [33.5731, -7.5898]; // Morocco
const DEFAULT_ZOOM = 6;

function hasValidCoords(c: Chantier): boolean {
  const lat = Number(c.latitude);
  const lng = Number(c.longitude);
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat !== 0 &&
    lng !== 0 &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

function fixLeafletDefaultIcon(): void {
  delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  });
}
fixLeafletDefaultIcon();

function MapFitBounds({ chantiers }: { chantiers: Chantier[] }) {
  const map = useMap();
  useEffect(() => {
    const withCoords = chantiers.filter(hasValidCoords);
    if (withCoords.length === 0) return;
    if (withCoords.length === 1) {
      const lat = Number(withCoords[0].latitude);
      const lng = Number(withCoords[0].longitude);
      map.setView([lat, lng], 14);
      return;
    }
    const bounds = L.latLngBounds(
      withCoords.map((c) => [Number(c.latitude), Number(c.longitude)] as [number, number])
    );
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [map, chantiers]);
  return null;
}

interface ChantierMapProps {
  chantiers: Chantier[];
  className?: string;
}

export default function ChantierMap({ chantiers, className = '' }: ChantierMapProps) {
  const withCoords = useMemo(() => chantiers.filter(hasValidCoords), [chantiers]);

  if (withCoords.length === 0) {
    return (
      <div
        className={`flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-500 ${className}`}
        style={{ minHeight: 320 }}
      >
        <p className="text-sm">Aucune coordonnée à afficher pour les chantiers.</p>
      </div>
    );
  }

  return (
    <div className={`overflow-hidden rounded-xl border border-slate-200 ${className}`} style={{ minHeight: 320 }}>
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        className="h-full w-full"
        style={{ height: 320 }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapFitBounds chantiers={withCoords} />
        {withCoords.map((c) => {
          const lat = Number(c.latitude);
          const lng = Number(c.longitude);
          const label = c.nom ?? c.code ?? `Chantier #${c.id}`;
          const sub = [c.localisation, c.code].filter(Boolean).join(' — ');
          return (
            <Marker key={c.id} position={[lat, lng]}>
              <Popup>
                <div className="min-w-[140px]">
                  <div className="font-semibold text-slate-900">{label}</div>
                  {sub && <div className="mt-0.5 text-xs text-slate-600">{sub}</div>}
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
